/**
 * API client for the Campaigns (fundraising) endpoints.
 *
 * Admin routes live under /api/campaigns/v1/... (bearer auth). The public
 * sponsor routes live under /api/campaigns/v1/public/... — the axios client
 * skips token injection for any URL containing `/public`, so those calls go
 * out unauthenticated.
 */

import apiClient from "./client";
import type {
  Campaign,
  CampaignWithItems,
  CampaignItem,
  CampaignListResponse,
  ContributionListResponse,
  CampaignSummary,
  ListCampaignsParams,
  ListContributionsParams,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  SetCampaignStatusRequest,
  CampaignItemInput,
  UpdateCampaignItemRequest,
  PublicCampaignPage,
  CreatePublicContributionRequest,
  PublicContributionResponse,
  ContributionStatusResponse,
} from "@/types/campaign";

export const campaignsApi = {
  // --- Admin: campaigns -----------------------------------------------------
  list: (params?: ListCampaignsParams) =>
    apiClient.get<CampaignListResponse>("/api/campaigns/v1", { params }),

  /** Fundraising roll-up for the admin dashboard (managers only). */
  summary: () => apiClient.get<CampaignSummary>("/api/campaigns/v1/summary"),

  get: (id: string) =>
    apiClient.get<CampaignWithItems>(`/api/campaigns/v1/${id}`),

  create: (data: CreateCampaignRequest) =>
    apiClient.post<CampaignWithItems>("/api/campaigns/v1", data),

  update: (id: string, data: UpdateCampaignRequest) =>
    apiClient.patch<CampaignWithItems>(`/api/campaigns/v1/${id}`, data),

  setStatus: (id: string, data: SetCampaignStatusRequest) =>
    apiClient.post<CampaignWithItems>(`/api/campaigns/v1/${id}/status`, data),

  remove: (id: string) =>
    apiClient.delete<{ message: string }>(`/api/campaigns/v1/${id}`),

  // --- Admin: items ---------------------------------------------------------
  addItem: (campaignId: string, data: CampaignItemInput) =>
    apiClient.post<CampaignItem>(`/api/campaigns/v1/${campaignId}/items`, data),

  updateItem: (
    campaignId: string,
    itemId: string,
    data: UpdateCampaignItemRequest,
  ) =>
    apiClient.patch<CampaignItem>(
      `/api/campaigns/v1/${campaignId}/items/${itemId}`,
      data,
    ),

  deleteItem: (campaignId: string, itemId: string) =>
    apiClient.delete<{ message: string }>(
      `/api/campaigns/v1/${campaignId}/items/${itemId}`,
    ),

  // --- Admin: contributions (sponsor list) ---------------------------------
  listContributions: (campaignId: string, params?: ListContributionsParams) =>
    apiClient.get<ContributionListResponse>(
      `/api/campaigns/v1/${campaignId}/contributions`,
      { params },
    ),

  /** Download the paid-sponsors CSV (server-rendered text/csv body). */
  exportContributions: (campaignId: string) =>
    apiClient.get<Blob>(
      `/api/campaigns/v1/${campaignId}/contributions/export`,
      { responseType: "blob" },
    ),

  // --- Public (no-login) ----------------------------------------------------
  getPublic: (token: string) =>
    apiClient.get<PublicCampaignPage>(`/api/campaigns/v1/public/${token}`),

  createPublicContribution: (
    token: string,
    data: CreatePublicContributionRequest,
  ) =>
    apiClient.post<PublicContributionResponse>(
      `/api/campaigns/v1/public/${token}/contributions`,
      data,
    ),

  getPublicContributionStatus: (token: string, contributionId: string) =>
    apiClient.get<ContributionStatusResponse>(
      `/api/campaigns/v1/public/${token}/contributions/${contributionId}/status`,
    ),
};

export default campaignsApi;

export type { Campaign };
